/*
 * Метаданные формы настроек «Авто-бот» (§1.11) + чистые помощники.
 * Никакой денежной логики — только показ/редактирование строковых значений конфига
 * (§1.2: фронт не считает деньги). Значения конфига приходят с границы как строки
 * (Decimal/enum) или числа (int) — форма держит их строками и шлёт изменённые поля.
 */

import type { BotConfigDto, LifecycleState, LinkStatus } from '@/lib/types'

export type FieldKind = 'number' | 'select' | 'text' | 'readonly'

export interface FieldMeta {
  key: string
  label: string
  hint: string
  kind: FieldKind
  suffix?: string
  options?: { value: string; label: string; disabled?: boolean }[]
  /** Защитный порог: применяется и к ОТКРЫТЫМ связкам сразу после «Сохранить» (§1.9). */
  protective?: boolean
  /** Направление ужесточения защитного порога (для анти-стампед предупреждения):
   *  'up' — больше = строже (min_liq_distance), 'down' — меньше = строже (max_loss). */
  tighten?: 'up' | 'down'
  /** Для text-списков: пустой ввод → ``null`` (а не ``[]``). Нужно для ``coin_allowlist``:
   *  пустой allowlist на бэке = «ничего не разрешено», а оператор ожидает «все» → шлём null. */
  emptyToNull?: boolean
  /** Целочисленное поле ``BotConfig`` (``int``): ввод без точки. Иначе ``int("5.0")`` на бэке
   *  бросит ValueError → SAVE_CONFIG провалится. Зеркалит тип поля в ``BotConfig``. */
  integer?: boolean
}

export interface FieldGroup {
  title: string
  fields: FieldMeta[]
}

/** Группы формы — §1.11 (пользовательские поля) + защитные/аудит-пороги (DoD аудит-2/ревью-4). */
export const SETTINGS_GROUPS: FieldGroup[] = [
  {
    title: 'Размер и плечо',
    fields: [
      {
        key: 'bundle_size_usd',
        label: 'Размер связки',
        suffix: '$',
        kind: 'number',
        hint: 'Целевой нотионал одной связки (на ногу) в USDT. Жёсткий верхний предел размера — итоговый размер не превысит это значение.',
      },
      {
        key: 'leverage',
        label: 'Плечо',
        suffix: '×',
        kind: 'number',
        hint: 'Кредитное плечо для расчёта маржи и постановки на бирже. Ограничено системным потолком ниже.',
      },
      {
        key: 'max_allowed_leverage',
        label: 'Системный потолок плеча',
        suffix: '×',
        kind: 'number',
        hint: 'Жёсткий cap эффективного плеча, независимый от поля «Плечо» (защита от случайного запуска с чрезмерным плечом). Системный параметр, не из обычных настроек.',
      },
      {
        key: 'deposit_utilization_pct',
        label: 'Задействованный депозит',
        suffix: '%',
        kind: 'number',
        protective: true,
        tighten: 'down',
        hint: 'Какую долю депозита можно задействовать суммарно. Контролируется на уровне маржи счёта.',
      },
      {
        key: 'trade_margin_pct',
        label: '% депозита на сделку',
        suffix: '%',
        kind: 'number',
        hint: 'Максимальная доля депозита под маржу одной сделки (per-trade cap, отдельно от общего задействования).',
      },
      {
        key: 'margin_buffer_pct',
        label: 'Запас маржи',
        suffix: '%',
        kind: 'number',
        protective: true,
        tighten: 'up',
        hint: 'Запас по марже при выборе плеча/размера — буфер против движения цены до ликвидации.',
      },
      {
        key: 'margin_mode',
        label: 'Режим маржи',
        kind: 'select',
        options: [
          { value: 'isolated', label: 'Isolated (изолированная)' },
          { value: 'cross', label: 'Cross (кросс) — недоступно в MVP', disabled: true },
        ],
        hint: 'Изолированная маржа (по умолчанию): ноги на разных биржах ликвидируются независимо. Cross в MVP запрещён.',
      },
    ],
  },
  {
    title: 'Вход и поиск',
    fields: [
      {
        key: 'max_open_trades',
        label: 'Макс. открытых сделок',
        kind: 'number',
        integer: true,
        hint: 'Сколько связок может быть открыто одновременно (по одной на монету).',
      },
      {
        key: 'minutes_to_funding',
        label: 'Время до фандинга',
        suffix: 'мин',
        kind: 'number',
        integer: true,
        hint: 'Минимум минут до ближайшего начисления фандинга, чтобы рассматривать монету для входа.',
      },
      {
        key: 'funding_spread_min',
        label: 'Мин. спред фандинга',
        suffix: '%',
        kind: 'number',
        hint: 'Минимальный нормализованный нетто-спред фандинга между биржами для входа (фильтр поиска возможностей).',
      },
      {
        key: 'min_profit',
        label: 'Мин. прибыль',
        suffix: '%',
        kind: 'number',
        hint: 'Минимальная ожидаемая чистая прибыль связки за горизонт удержания (после всех издержек).',
      },
      {
        key: 'profit_margin',
        label: 'Запас прибыли',
        suffix: '%',
        kind: 'number',
        hint: 'Запас над порогом прибыли (рекомендуется 20–50%): ожидаемая прибыль должна превышать порог с этим запасом.',
      },
      {
        key: 'min_liquidity_usd',
        label: 'Мин. ликвидность',
        suffix: '$',
        kind: 'number',
        hint: 'Минимальный суточный объём монеты (USDT) для допуска к входу.',
      },
      {
        key: 'refresh_interval_s',
        label: 'Интервал обновления',
        suffix: 'с',
        kind: 'number',
        integer: true,
        hint: 'Период сканера: пересбор кандидатов/парсера и попытки входа. Надзор открытых связок работает на своей (более частой) частоте независимо.',
      },
      {
        key: 'entry_as',
        label: 'Вход как',
        kind: 'readonly',
        hint: 'Тип входа — тейкер (рыночный ордер). Мейкер появится позже (отключено).',
      },
    ],
  },
  {
    title: 'Исполнение',
    fields: [
      {
        key: 'order_parts',
        label: 'Частей ордера',
        kind: 'number',
        integer: true,
        hint: 'На сколько частей дробить ордер каждой ноги (снижение price-impact).',
      },
      {
        key: 'max_price_impact_pct',
        label: 'Макс. price impact',
        suffix: '%',
        kind: 'number',
        hint: 'Допустимое влияние на цену при проходе по стакану на входе. На выходе порог умножается на множитель выхода.',
      },
      {
        key: 'slippage_buffer_pct',
        label: 'Буфер проскальзывания',
        suffix: '%',
        kind: 'number',
        hint: 'Дополнительный запас на проскальзывание, закладываемый в расчёт прибыли.',
      },
      {
        key: 'max_liquidity_fraction_pct',
        label: 'Макс. доля ликвидности',
        suffix: '%',
        kind: 'number',
        hint: 'Какую долю доступной ликвидности стакана можно выбрать одним ордером.',
      },
      {
        key: 'pre_send_price_drift_pct',
        label: 'Дрейф цены до отправки',
        suffix: '%',
        kind: 'number',
        hint: 'Максимальное расхождение цены между расчётом и отправкой ордера — повторная проверка перед постановкой (входной путь, не hot-apply к открытым).',
      },
      {
        key: 'max_position_skew_pct',
        label: 'Макс. перекос позиции',
        suffix: '%',
        kind: 'number',
        hint: 'Допустимый перекос размеров ног (только от частичных филлов) — сверх него выравнивание/нейтрализация.',
      },
      {
        key: 'exit_impact_multiplier',
        label: 'Множитель impact выхода',
        suffix: '×',
        kind: 'number',
        hint: 'Во сколько раз порог price-impact на выходе больше входного (выход обычно дороже).',
      },
    ],
  },
  {
    title: 'Риск и защита',
    fields: [
      {
        key: 'max_loss_pct',
        label: 'Макс. убыток по сделке',
        suffix: '%',
        kind: 'number',
        protective: true,
        tighten: 'down',
        hint: 'Стоп-лосс на связку: при убытке свыше — аварийный выход. Применяется к открытым связкам сразу после «Сохранить».',
      },
      {
        key: 'min_liq_distance_pct',
        label: 'Мин. расстояние до ликвидации',
        suffix: '%',
        kind: 'number',
        protective: true,
        tighten: 'up',
        hint: 'Минимальный запас по расстоянию до цены ликвидации (по mark). Применяется к открытым связкам сразу.',
      },
      {
        key: 'min_account_margin_buffer_pct',
        label: 'Мин. запас маржи счёта',
        suffix: '%',
        kind: 'number',
        protective: true,
        tighten: 'up',
        hint: 'Минимальный буфер маржи счёта (две ступени). Применяется к открытым связкам сразу.',
      },
      {
        key: 'max_mark_divergence_pct',
        label: 'Макс. расхождение mark',
        suffix: '%',
        kind: 'number',
        protective: true,
        tighten: 'down',
        hint: 'Максимальное расхождение mark-цен ног (защита от фантомного спреда). Сверх — отказ входа / защитная переоценка.',
      },
      {
        key: 'max_clock_drift_ms',
        label: 'Макс. дрейф часов',
        suffix: 'мс',
        kind: 'number',
        integer: true,
        protective: true,
        tighten: 'down',
        hint: 'Допустимый дрейф часов относительно биржи; сверх — стоп новых входов.',
      },
      {
        key: 'max_daily_loss_usd',
        label: 'Дневной лимит убытка',
        suffix: '$',
        kind: 'number',
        protective: true,
        tighten: 'down',
        hint: 'Дневной лимит реализованного убытка по счёту; пробитие → стоп всех новых входов.',
      },
      {
        key: 'max_fee_drift_pct',
        label: 'Макс. дрейф комиссий',
        suffix: '%',
        kind: 'number',
        protective: true,
        tighten: 'down',
        hint: 'Допустимое расхождение фактических комиссий от закешированных; сверх — алерт FEE_DRIFT.',
      },
      {
        key: 'coin_denylist',
        label: 'Чёрный список монет',
        kind: 'text',
        hint: 'Монеты, исключённые из торговли (через запятую, напр. «LUNA, FTT»). Ручное решение оператора, не авто-блэклист.',
      },
      {
        key: 'coin_allowlist',
        label: 'Белый список монет',
        kind: 'text',
        emptyToNull: true,
        hint: 'Если задан — торгуются ТОЛЬКО эти монеты (через запятую). Пусто = разрешены все (кроме чёрного списка). Ручное решение оператора.',
      },
    ],
  },
  {
    title: 'Парсер',
    fields: [
      {
        key: 'parser_min_volume_usd',
        label: 'Мин. объём (парсер)',
        suffix: '$',
        kind: 'number',
        hint: 'Минимальный суточный объём для отображения монеты в таблице парсинга.',
      },
      {
        key: 'parser_display_limit',
        label: 'Показывать строк',
        kind: 'number',
        integer: true,
        hint: 'Сколько строк (топ-N) отдавать в снимке парсера.',
      },
      {
        key: 'parser_funding_highlight_pct',
        label: 'Подсветка «Фандинг >X%»',
        suffix: '%',
        kind: 'number',
        hint: 'Порог информационной метрики подсветки размаха фандинга в парсере (не порог входа).',
      },
    ],
  },
]

/** Все ключи формы (для извлечения изменений). */
export const SETTINGS_KEYS: string[] = SETTINGS_GROUPS.flatMap((g) => g.fields.map((f) => f.key))

/** Привести значение конфига к строке для поля ввода. Массивы → через запятую. */
export function configValueToString(value: BotConfigDto[string] | undefined): string {
  if (value == null) return ''
  if (Array.isArray(value)) return value.join(', ')
  return String(value)
}

/** Разобрать введённую строку обратно в значение payload по виду поля. */
export function fieldInputToPayload(meta: FieldMeta, raw: string): BotConfigDto[string] {
  const trimmed = raw.trim()
  if (meta.kind === 'text') {
    // Список монет: через запятую → массив верхнего регистра.
    const items = trimmed
      .split(',')
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean)
    // coin_allowlist: пусто → null («все»), а не [] («ничего не разрешено» на бэке).
    if (items.length === 0 && meta.emptyToNull) return null
    return items
  }
  // Числа/select/прочее шлём строкой — бэкенд (ConfigLoader) коэрсит к Decimal/int/enum.
  return trimmed
}

/** Изменилось ли поле относительно исходного значения конфига (и можно ли его отправлять). */
export function isFieldChanged(meta: FieldMeta, current: string, original: string): boolean {
  if (meta.kind === 'text') {
    const norm = (s: string) =>
      s
        .split(',')
        .map((x) => x.trim().toUpperCase())
        .filter(Boolean)
        .join(',')
    return norm(current) !== norm(original)
  }
  if (meta.kind === 'number') {
    // Пустое/невалидное число НЕ считаем изменением: иначе в payload уйдёт "" и бэкенд
    // упадёт на Decimal("")/int("") (COMMAND_FAILED). Очистка поля → держим текущее значение.
    const a = Number(current)
    if (!Number.isFinite(a) || current.trim() === '') return false
    const b = Number(original)
    // «15» и «15.0» — одно значение (числовое сравнение).
    if (Number.isFinite(b)) return a !== b
    return current.trim() !== original.trim()
  }
  // select / readonly — строковое сравнение.
  return current.trim() !== original.trim()
}

/** Ужесточается ли защитный порог (для анти-стампед предупреждения, §1.13 доп.). */
export function isProtectiveTightened(meta: FieldMeta, current: string, original: string): boolean {
  if (!meta.protective || !meta.tighten) return false
  const a = Number(current)
  const b = Number(original)
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false
  return meta.tighten === 'up' ? a > b : a < b
}

const LIFECYCLE_LABELS: Record<LifecycleState, string> = {
  stopped: 'Бот выключен',
  starting: 'Запуск…',
  running: 'Бот работает',
  stopping: 'Остановка…',
}

export function lifecycleLabel(state: LifecycleState): string {
  return LIFECYCLE_LABELS[state]
}

const LINK_STATUS_LABELS: Record<LinkStatus, string> = {
  created: 'Создана',
  entering: 'Вход',
  active: 'Активна',
  exiting: 'Выход',
  flattening: 'Закрытие',
  neutralizing: 'Нейтрализация',
  stuck_directional: 'Застряла (направленная)',
  closed: 'Закрыта',
}

export function linkStatusLabel(status: LinkStatus): string {
  return LINK_STATUS_LABELS[status] ?? status
}

/** Тон бейджа статуса связки. */
export function linkStatusTone(status: LinkStatus): 'accent' | 'pos' | 'warn' | 'neg' | 'neutral' {
  if (status === 'active') return 'pos'
  if (status === 'stuck_directional') return 'neg'
  if (status === 'neutralizing' || status === 'flattening') return 'warn'
  if (status === 'entering' || status === 'exiting') return 'accent'
  return 'neutral'
}
